// Single source of truth for editorial articles ("The Edit").
// URL shape: /{category}/{slug}/  — no "blog" prefix, no "edit" prefix.
// The old /blog/{oldSlug}/ URLs are 301'd to the new location by the server + Cloudflare _redirects.

export const CATEGORIES = {
  planning:      { label: "Planning",      color: "ocean"   },
  families:      { label: "Families",      color: "sunset"  },
  budget:        { label: "Budget",        color: "sand"    },
  experiences:   { label: "Experiences",   color: "ocean"   },
  culture:       { label: "Culture",       color: "sunset"  },
  neighborhoods: { label: "Neighborhoods", color: "ocean"   }
};

// Bodies are the original guides, recovered from the archived site in
// recovery/pages/compiled-site/blog/ and restored with proper Hawaiian
// orthography. They are the fallback: a published post from the Edit worker
// wins on slug collision (see mergeArticles), so editing one there overrides
// what is written here.
export const ARTICLES = [
  {
    slug: "best-time-to-visit-hawaii",
    category: "planning",
    tag: "Planning",
    title: "When is the best time to visit Hawaiʻi?",
    excerpt: "Each month brings its own magic — from whale season on Maui to Kauaʻi's dry summer. Here's how to time your trip for what matters most to you.",
    img: "https://blog.aloharates.com/apr18.webp",
    date: "2026-04-18",
    legacyBlog: "best-time-to-visit-hawaii",
    body: [
      "<p>Hawaiʻi is a year-round destination — but ask any local and they'll tell you certain weeks are pure magic, and others are best avoided. Here's how to think about timing.</p>",
      "<h2>The shoulder seasons win</h2>",
      "<p>Mid-April through early June and September through mid-November bring the best combination of weather, prices, and crowd levels. Trade winds keep things comfortable, ocean conditions are gentle, and you'll often save 20–40% versus winter holiday rates.</p>",
      "<h2>Winter (Dec–Mar): big surf and big crowds</h2>",
      "<p>Winter is high season — both for visitors escaping cold weather and for North Shore surf. If you're chasing whale watching on Maui (December–April peak), you'll find the best sightings now. Just book 4–6 months ahead and expect premium pricing during Christmas, New Year, and Presidents Week.</p>",
      "<h2>Summer (Jun–Aug): families and dry weather</h2>",
      "<p>Summer is peak family season. Expect dry, sunny weather (especially on the leeward sides of each island) and a second pricing peak around July 4th. Kauaʻi's North Shore is at its best for snorkeling — winter swells are gone.</p>",
      "<h2>Bottom line</h2>",
      "<p>Aim for late April, early May, late September, or early October if you can swing it. You'll get the same Hawaiʻi everyone falls in love with — minus the crowds and at a fraction of the cost.</p>"
    ].join("")
  },
  {
    slug: "maui-road-to-hana-guide",
    category: "experiences",
    tag: "Maui",
    title: "The complete Road to Hāna guide",
    excerpt: "64 miles, 600 turns, and dozens of waterfalls. The stops you cannot miss — and the ones to skip — on Hawaiʻi's most legendary drive.",
    img: "https://blog.aloharates.com/apr12.webp",
    date: "2026-04-12",
    legacyBlog: "maui-road-to-hana-guide",
    body: [
      "<p>The Road to Hāna is 64 miles, 600+ turns, and 50 one-lane bridges of pure Maui magic. Done right, it's a full day of waterfalls, black-sand beaches, and roadside food stands. Done wrong, it's six hours of car sickness.</p>",
      "<h2>Start early — like really early</h2>",
      "<p>Be on the road by 7 a.m. The drive itself takes 2.5 hours each way without stops. Add stops, lunch, and the return drive in daylight, and you need every hour you can get. Tour buses start hitting the road around 9 — beat them.</p>",
      "<h2>Must-stop spots</h2>",
      "<p>Twin Falls (mile 2) for an easy waterfall warm-up. Keʻanae Peninsula (mile 16) for dramatic lava cliffs. Waiʻānapanapa State Park (mile 32) for the famous black sand beach — reservations required. The Pools of ʻOheʻo (mile 42) for swimming holes if open.</p>",
      "<h2>Skip these unless you have time</h2>",
      "<p>The Garden of Eden Arboretum is pretty but pricey. Hoʻokipa Lookout is better on the way back. Most of the marked viewpoints between miles 30 and 35 are interchangeable — pick one or two.</p>",
      "<h2>Practical tips</h2>",
      "<p>Fill the tank in Pāʻia. Bring cash for roadside fruit stands. Download offline maps — service drops out for long stretches. And drive slowly: the road is everything, the destination is just where you turn around.</p>"
    ].join("")
  },
  {
    slug: "family-friendly-hawaii",
    category: "families",
    tag: "Families",
    title: "Best family-friendly Hawaiʻi rentals",
    excerpt: "Kid-approved beaches, pool resorts with kids' clubs, and the rentals that nail group travel. A guide for parents who want a real vacation too.",
    img: "https://blog.aloharates.com/apr6.webp",
    date: "2026-04-06",
    legacyBlog: "family-friendly-hawaii",
    body: [
      "<p>Hawaiʻi with kids can be the best trip of your life — or a hot, sandy disaster. The difference is in the planning. Here's what works.</p>",
      "<h2>Pick the right island</h2>",
      "<p>Oʻahu and Maui are the easiest with kids — best infrastructure, calmest swimming beaches, and the most variety. Big Island works for adventurous families with older kids (volcanoes, stargazing). Save Kauaʻi's North Shore for when the children are confident swimmers.</p>",
      "<h2>Best calm swimming beaches</h2>",
      "<p>Ko Olina lagoons (Oʻahu) — manmade calm-water lagoons. Wailea Beach (Maui) — gentle south shore. ʻAnaehoʻomalu Bay (Big Island) — protected and shallow. Avoid winter North Shore beaches anywhere — the surf is for pros, not kids.</p>",
      "<h2>Vacation rentals vs. resorts</h2>",
      "<p>Resorts win for families who want kids' clubs, pools, and on-site food. Vacation rentals win for cost, kitchens (huge for picky eaters and breakfasts), and laundry. For trips longer than 5 nights with kids under 8, a condo with a kitchen usually pays off.</p>",
      "<h2>Budget hack: the 4 p.m. dip</h2>",
      "<p>Hit the beach at 4 p.m. when day-trippers leave. The sun is gentler, parking opens up, and you'll get the prettiest light — plus an easier bedtime since the kids are wiped out.</p>"
    ].join("")
  },
  {
    slug: "hawaii-budget-tips",
    category: "budget",
    tag: "Budget",
    title: "10 ways to do Hawaiʻi on a budget",
    excerpt: "You don't need a resort budget to live your aloha dream. Locals' tips on saving on flights, food, and stays without compromising the trip.",
    img: "https://blog.aloharates.com/mar30.webp",
    date: "2026-03-30",
    legacyBlog: "hawaii-budget-tips",
    body: [
      "<p>Hawaiʻi has a reputation for being expensive — and yeah, it can be. But locals will tell you it's also one of the most rewarding places to visit on a budget if you know how to play it.</p>",
      "<h2>1. Travel in shoulder season</h2>",
      "<p>Late April–early June and September–early November can cut your hotel and flight costs by a third versus peak winter or summer.</p>",
      "<h2>2. Stay in a condo with a kitchen</h2>",
      "<p>Eating every meal out adds up fast — easily $150–250/day for a family. A condo lets you do breakfast and a few dinners in. Costco runs are a Hawaiʻi vacation tradition.</p>",
      "<h2>3. Hit Costco for fuel and food</h2>",
      "<p>Costco gas is the cheapest on every island. Their poke, prepared foods, and rotisserie chickens are excellent and feed a family for a fraction of resort prices.</p>",
      "<h2>4. Beach all the things</h2>",
      "<p>Hawaiʻi's beaches are free and they are world class. Pack your own snorkel gear from the mainland — daily rentals add up, and most resort gear is mediocre.</p>",
      "<h2>5. Skip the lūʻau (or do one cheap)</h2>",
      "<p>Resort lūʻau run $150+ per adult. Smaller community-run versions are half the price and often more authentic.</p>",
      "<h2>6. Public parks have the best views</h2>",
      "<p>Hāpuna Beach Park, Polihale, Hanalei Bay — many of Hawaiʻi's best beaches are state or county parks with $5–10 parking fees. The view doesn't get any better at the resort next door.</p>",
      "<h2>7–10: Quick hits</h2>",
      "<p>Drive a small economy car. Book activities directly with operators, not resort concierges. Use Aloha Rates to compare every channel for the same room — supplier prices vary by 20–40% on the same dates. And consider a midweek arrival — Sunday-to-Sunday rentals often charge a premium.</p>"
    ].join("")
  }
];

export const articleHref = a => `/${a.category}/${a.slug}/`;
export const findArticle = (category, slug) => ARTICLES.find(a => a.category === category && a.slug === slug) || null;
export const findByLegacyBlogSlug = slug => ARTICLES.find(a => a.legacyBlog === slug) || null;

// Normalize a remote article record from the admin worker into the shape our components render.
// The worker stores `{slug, title, excerpt, category, date, img, imgPosition, body}`.
// Category is already the URL slug ("planning", "families", ...) so articleHref works as-is.
export function normalizeRemote(post) {
  if (!post || !post.slug || !post.title) return null;
  const catKey = (post.category || "planning").toLowerCase();
  const tag = CATEGORIES[catKey]?.label || (post.category || "Journal");
  return {
    slug: post.slug,
    category: catKey,
    tag,
    title: post.title,
    excerpt: post.excerpt || "",
    img: post.img || "",
    imgPosition: post.imgPosition || "center center",
    date: post.date || "",
    body: post.body || "",
    remote: true
  };
}

// Merge live articles from the worker with local fallbacks — remote wins on slug collision.
export function mergeArticles(remote, fallbacks = ARTICLES) {
  const normalized = (remote || []).map(normalizeRemote).filter(Boolean);
  const seen = new Set(normalized.map(a => a.slug));
  return [...normalized, ...fallbacks.filter(a => !seen.has(a.slug))];
}
